import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { FinancialAccount } from "@common/types/financialAccount";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { OptionGroupItem } from "@design-system/OptionGroup/OptionGroupItem";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { OptionGroupCustomItem } from "@design-system/OptionGroup/OptionGroupCustomItem";
import { integrationConnectionQueryKeys } from "@api/integrationConnection.api";
import { useProcessorConnectionStatus } from "../../useProcessorConnectionStatus";
import {
    financialConnectionsQueryKey,
    queryClient,
} from "../../../../queryClient";
import { CreateStripeConnection } from "../../../stripe/CreateStripeConnection";
import { ConnectPlaid } from "../../../plaid/ConnectPlaid/ConnectPlaid";
import { Footer } from "../../components/Footer";
import commonStyles from "../common.module.scss";
import animations from "../../layout/OnboardingLayout.module.scss";
import { PersonalDataAlert } from "../../components/PersonalDataAlert";
import { CreatePayPalConnection } from "../../../paypal/CreatePayPalConnection";

interface Props {
    initialValue: string;
    financialAccounts: FinancialAccount[];
    onSubmit(requested: string): void;
    back(): void;
}

export const PaymentProcessor: React.FC<Props> = ({
    onSubmit,
    back,
    financialAccounts,
    initialValue,
}) => {
    const [requested, setRequested] = useState<string>(initialValue);

    const {
        venmoConnected,
        paypalConnected,
        stripeConnected,
        cashAppConnected,
        hasAnyProcessorConnected,
    } = useProcessorConnectionStatus(financialAccounts);

    const handleConnected = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [financialConnectionsQueryKey],
        });
        queryClient.invalidateQueries({
            queryKey: [integrationConnectionQueryKeys.all],
        });
    }, []);

    const isPristine = !hasAnyProcessorConnected && !requested;
    const handleNext = useCallback(
        () => onSubmit(requested),
        [onSubmit, requested],
    );

    useKeyboardCommand({
        keys: "enter",
        callback: () => {
            handleNext();
        },
        options: {
            enabled: !isPristine,
            description: "Continue",
        },
    });

    return (
        <div
            className={classNames(animations.fadeIn1, commonStyles.content780)}
        >
            <h1 className={classNames(commonStyles.title, animations.fadeIn2)}>
                Select payment processor
            </h1>

            <PersonalDataAlert />

            <CreateStripeConnection onCreated={handleConnected}>
                {({ initializeConnection }) => (
                    <OptionGroupItem
                        icon={
                            <InstitutionIcon institution="stripe" size="lg" />
                        }
                        title="Stripe"
                        selected={stripeConnected}
                        onToggle={initializeConnection}
                    />
                )}
            </CreateStripeConnection>

            <CreatePayPalConnection onCreated={handleConnected}>
                {({ initializeConnection }) => (
                    <OptionGroupItem
                        icon={
                            <InstitutionIcon institution="paypal" size="lg" />
                        }
                        title="PayPal"
                        selected={paypalConnected}
                        onToggle={initializeConnection}
                    />
                )}
            </CreatePayPalConnection>

            <ConnectPlaid onConnected={handleConnected}>
                {({ initializeConnection }) => (
                    <>
                        <OptionGroupItem
                            icon={
                                <InstitutionIcon
                                    institution="venmo"
                                    size="lg"
                                />
                            }
                            title="Venmo"
                            selected={venmoConnected}
                            onToggle={initializeConnection}
                        />
                        <OptionGroupItem
                            icon={
                                <InstitutionIcon
                                    institution="cash-app"
                                    size="lg"
                                />
                            }
                            title="Cash App"
                            selected={cashAppConnected}
                            onToggle={initializeConnection}
                        />
                    </>
                )}
            </ConnectPlaid>

            <OptionGroupCustomItem
                value={requested}
                empty="Other"
                onChange={setRequested}
            />
            <OptionGroupItem
                title="No payment processor"
                selected={false}
                onToggle={handleNext}
            />

            <Footer onBack={back}>
                <WithHotkey
                    hotKey="enter"
                    hotKeyOptions={{
                        description: "Continue",
                        enabled: !isPristine,
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            onClick={handleNext}
                            size="xl"
                            rightIcon={hotkeyHint}
                        >
                            Next
                        </Button>
                    )}
                </WithHotkey>
            </Footer>
        </div>
    );
};
